import {
    Input,
    ALL_FORMATS,
    BlobSource,
    AudioBufferSink,
} from 'mediabunny';

export type PeaksMeta = {
    durationSec: number;
    buckets: number;
    windowSec: number;
    quantization: 'f32_amp';
};

export type PeaksResult = { basePeaks: Float32Array; meta: PeaksMeta };

export type LufsResult = {
    integratedLufs: number;     // Integrated loudness (e.g., -22.5 LUFS)
    recommendedGain: number;    // Linear gain to hit target LUFS (e.g., 2.0)
    truePeak: number;           // Maximum sample value
};

export type PeaksWithLufsResult = PeaksResult & { lufs: LufsResult };

// Target loudness for normalization (podcast/streaming standard)
const TARGET_LUFS = -16;

// LUFS gating thresholds per ITU-R BS.1770
const ABSOLUTE_GATE_LUFS = -70;  // Blocks below this are always excluded
const RELATIVE_GATE_DB = -10;    // Blocks more than 10dB below mean are excluded
const BLOCK_SIZE_MS = 400;       // 400ms blocks per ITU standard

type Opts = {
    pixelsPerSecond?: number;   // default 100  (like --pixels-per-second)
    bits?: 8 | 16;              // default 8     (like --bits)
    maxBuckets?: number;        // default 20000 (cap for long inputs)
    decodeRatePerSecond?: number; // default 12  (~chunks/sec to decode)
};

export function EncodePeaksToDat(peaksF32: Float32Array): ArrayBuffer {
    const buckets = peaksF32.length;
    const buf = new ArrayBuffer(buckets * 2);
    const dv = new DataView(buf);

    for (let i = 0; i < buckets; i++) {
        const v = Math.max(0, Math.min(1, peaksF32[i] || 0)); // clamp
        dv.setUint16(i * 2, Math.round(v * 65535), true);
    }

    return buf;
}

export function DecodePeaksDatToFloat32(buf: ArrayBuffer): Float32Array {
    const u16 = new Uint16Array(buf);
    const out = new Float32Array(u16.length);

    for (let i = 0; i < u16.length; i++) {
        out[i] = u16[i] / 65535;
    }

    return out;
}

/**
 * Compute full-detail waveform AND LUFS loudness analysis in a single pass.
 */
export async function ComputeAudioPeaksWithLufs(
    file: File | Blob,
    opts?: {
        pixelsPerSecond?: number;
        maxDurationSec?: number;
        onProgress?: (p: { basePeaks: Float32Array; durationSec: number; processedUntilSec: number }) => void;
        progressInitialSec?: number;
        progressEverySec?: number;
    }
): Promise<PeaksWithLufsResult> {
    // Higher bucket density improves perceived waveform detail (closer to Descript),
    // but has memory implications for very long files (peaks + touched arrays).
    const pixelsPerSecond = opts?.pixelsPerSecond ?? 800;
    const MAX_DURATION_SEC = opts?.maxDurationSec ?? 3 * 60 * 60; // 3 hours
    const maxBuckets = MAX_DURATION_SEC * pixelsPerSecond;

    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
    const a = await input.getPrimaryAudioTrack();
    if (!a) throw new Error('No audio stream found');

    const durationSec: number = await a.computeDuration();
    const buckets = Math.min(Math.ceil(durationSec * pixelsPerSecond), maxBuckets);

    const meta: PeaksMeta = {
        durationSec,
        buckets,
        windowSec: durationSec / Math.max(1, buckets),
        quantization: 'f32_amp',
    };

    const peaks = new Float32Array(buckets);
    const touched = new Uint8Array(buckets);

    // LUFS calculation: collect 400ms block mean-squares for gating
    let truePeak = 0;
    let currentSampleRate = 48000; // Will be updated from first buffer
    const blockMeanSquares: number[] = [];
    let blockSumSquares = 0;
    let blockSampleCount = 0;
    let blockSamplesNeeded = 0; // Computed once we know sample rate

    const sink = new AudioBufferSink(a);
    let lastTS = 0;
    const onProgress = opts?.onProgress;
    const progressEverySec = Math.max(1, Number(opts?.progressEverySec ?? 600)); // default: every 10min of content
    const progressInitialSec = Math.max(0, Number(opts?.progressInitialSec ?? 10)); // initial early update
    let nextProgressSec = progressInitialSec > 0 ? progressInitialSec : progressEverySec;

    for await (const { buffer, timestamp } of sink.buffers(0)) {
        const ch = buffer.numberOfChannels;
        const sr = buffer.sampleRate;
        const nf = buffer.length;

        // Initialize block size on first buffer
        if (blockSamplesNeeded === 0) {
            currentSampleRate = sr;
            blockSamplesNeeded = Math.floor(sr * BLOCK_SIZE_MS / 1000);
        }

        for (let i = 0; i < nf; i++) {
            let sum = 0;
            for (let c = 0; c < ch; c++) {
                const sample = buffer.getChannelData(c)[i];
                sum += sample;
                // Track true peak across all channels
                const absSample = Math.abs(sample);
                if (absSample > truePeak) truePeak = absSample;
            }
            const avg = sum / ch;
            const s = Math.abs(avg);

            // Accumulate for LUFS block
            blockSumSquares += avg * avg;
            blockSampleCount++;

            // When block is full, store its mean square and reset
            if (blockSampleCount >= blockSamplesNeeded) {
                const blockMeanSquare = blockSumSquares / blockSampleCount;
                blockMeanSquares.push(blockMeanSquare);
                blockSumSquares = 0;
                blockSampleCount = 0;
            }

            // Waveform peaks
            const tSec = (timestamp ?? lastTS) + i / sr;
            const bi = Math.min(buckets - 1, Math.max(0, Math.floor((tSec / Math.max(1e-6, durationSec)) * buckets)));
            if (!touched[bi]) { peaks[bi] = s; touched[bi] = 1; }
            else if (s > peaks[bi]) { peaks[bi] = s; }
        }
        lastTS = (timestamp ?? lastTS) + nf / sr;

        if (onProgress && lastTS >= nextProgressSec - 1e-3) {
            try {
                onProgress({
                    basePeaks: peaks,
                    durationSec,
                    processedUntilSec: Math.min(durationSec, Math.max(0, lastTS)),
                });
            } catch {}
            while (nextProgressSec <= lastTS + 1e-3) {
                nextProgressSec += progressEverySec;
            }
        }
    }

    // Don't forget the last partial block if it has significant samples
    if (blockSampleCount > blockSamplesNeeded / 4) {
        const blockMeanSquare = blockSumSquares / blockSampleCount;
        blockMeanSquares.push(blockMeanSquare);
    }

    backfillF32(peaks, touched);

    if (onProgress) {
        try {
            onProgress({ basePeaks: peaks, durationSec, processedUntilSec: durationSec });
        } catch {}
    }

    // Calculate gated LUFS per ITU-R BS.1770
    const lufsOffset = -0.691;

    // Convert block mean squares to LUFS values
    const blockLufs = blockMeanSquares.map(ms =>
        ms > 1e-12 ? lufsOffset + 10 * Math.log10(ms) : -70
    );

    // Step 1: Absolute gate - exclude blocks below -70 LUFS
    const afterAbsoluteGate = blockMeanSquares.filter((_, i) => blockLufs[i] > ABSOLUTE_GATE_LUFS);
    const afterAbsoluteGateLufs = blockLufs.filter(l => l > ABSOLUTE_GATE_LUFS);

    let integratedLufs = -70;
    if (afterAbsoluteGate.length > 0) {
        // Step 2: Compute mean of blocks passing absolute gate
        const absoluteGatedMean = afterAbsoluteGate.reduce((a, b) => a + b, 0) / afterAbsoluteGate.length;
        const absoluteGatedLufs = absoluteGatedMean > 1e-12
            ? lufsOffset + 10 * Math.log10(absoluteGatedMean)
            : -70;

        // Step 3: Relative gate - exclude blocks more than 10dB below the absolute-gated mean
        const relativeThreshold = absoluteGatedLufs + RELATIVE_GATE_DB;
        const afterRelativeGate = afterAbsoluteGate.filter((_, i) => afterAbsoluteGateLufs[i] > relativeThreshold);

        if (afterRelativeGate.length > 0) {
            // Final integrated loudness from blocks passing both gates
            const finalMean = afterRelativeGate.reduce((a, b) => a + b, 0) / afterRelativeGate.length;
            integratedLufs = finalMean > 1e-12
                ? lufsOffset + 10 * Math.log10(finalMean)
                : -70;
        } else {
            integratedLufs = absoluteGatedLufs;
        }
    }

    // Calculate recommended gain to reach TARGET_LUFS (-16)
    const gainDb = TARGET_LUFS - integratedLufs;
    // Clamp gain to reasonable range (0.1x to 10x)
    const recommendedGain = Math.max(0.1, Math.min(10, Math.pow(10, gainDb / 20)));

    const lufs: LufsResult = {
        integratedLufs,
        recommendedGain,
        truePeak,
    };

    return { basePeaks: peaks, meta, lufs };
}

function backfillF32(peaks: Float32Array, touched: Uint8Array) {
    let last = 0;
    for (let i = 0; i < touched.length; i++) {
        if (!touched[i]) peaks[i] = last; else last = peaks[i];
    }
    let next = 0;
    for (let i = touched.length - 1; i >= 0; i--) {
        if (!touched[i]) peaks[i] = next; else next = peaks[i];
    }
}
